import type { Fetch } from "@colossus/api-client";
import { browser } from "$app/env";
import { pinToday } from "../calendar.ts";
import { perReader, reader } from "../reader.ts";
import { DEMO_TENANT } from "./replay.ts";
import type { Tell } from "./router.ts";
import { loadSnapshot } from "./snapshot.ts";

const DEMO_ROOT = `/t/${DEMO_TENANT}`;

const DEMO_RECORD = `/api/tenants/${DEMO_TENANT}`;

const under = (path: string | undefined, root: string): boolean =>
  path === root || path?.startsWith(`${root}/`) === true;

let heading: string | undefined;

export const headFor = (path: string | undefined): void => {
  heading = path;
};

export const crossesDemo = (from: string | undefined, to: string | undefined): boolean =>
  under(from, DEMO_ROOT) !== under(to, DEMO_ROOT);

export const showingDemo = (): boolean => under(heading ?? reader().pathname(), DEMO_ROOT);

export const inDemo = (request: Request): boolean => {
  const asked = new URL(request.url).pathname;
  return under(asked, DEMO_ROOT) || under(asked, DEMO_RECORD) || showingDemo();
};

const parts = () =>
  Promise.all([loadSnapshot(), import("./backend.ts"), import("./overlay.svelte.ts")]);

let loading: ReturnType<typeof parts> | null = null;

const loaded = (): ReturnType<typeof parts> => {
  loading ??= parts();
  return loading;
};

const backendNow = perReader((): { backend: Promise<Fetch> | null } => ({ backend: null }));

const backendFor = (onDemo: boolean, tell: Tell): Promise<Fetch> =>
  loaded().then(([snapshot, { demoBackend }, { demoOverlay, pristineOverlay }]) => {
    if (onDemo) pinToday(snapshot.today);
    return demoBackend(snapshot, onDemo && browser ? demoOverlay() : pristineOverlay(), tell);
  });

export const demoFetch =
  (tell: Tell): Fetch =>
  async (request) => {
    const held = backendNow();
    held.backend ??= backendFor(under(reader().pathname(), DEMO_ROOT), tell);
    return (await held.backend)(request);
  };

if (under(globalThis.location?.pathname, DEMO_ROOT)) void loaded();
